import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import GoogleButton from '../../../../CreateAccountModal/CreateAccountModalFormArea/GoogleButton/GoogleButton';
import Office365Button from '../../../../CreateAccountModal/CreateAccountModalFormArea/Office365Button/Office365Button';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';

import WpAlert from 'Wpds/components/alert/WpAlert';

import { updateOnboardingState } from 'Shared/actions/onboardingState';
import { DataSourceSlugs } from 'Shared/constants/types';
import { trackClickedConnectApp } from 'Shared/tracking/tracking';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

type Props = {
  cannotSignIntoGoogle: boolean;
  openWindow?: boolean;
};

function ConnectCalendarButtons({ cannotSignIntoGoogle, openWindow = false }: Props) {
  const {
    app: {
      routes: { googleCalendarSignupProfileScopeUrl, office365CalendarSignupUrl },
    },
  } = useApp();

  const [windowRef, setWindowRef] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const dispatch = useDispatch();

  const receiveMessage = event => {
    // if (event.origin !== window.origin) {
    //  return;
    //}

    if (event.data?.alert) {
      setErrorMessage(event.data?.alert);
    } else {
      dispatch(updateOnboardingState({}));
    }
  };

  const openSignInWindow = (url, name) => {
    // remove any existing event listeners
    window.removeEventListener('message', receiveMessage);

    // center the window
    const window_w = 500;
    const window_h = 740;
    const window_x = window.top.outerWidth / 2 + window.top.screenX - window_w / 2;
    const window_y = window.top.outerHeight / 2 + window.top.screenY - window_h / 2;
    const strWindowFeatures = `toolbar=no, menubar=no, width=${window_w}, height=${window_h}, top=${window_y}, left=${window_x}`;

    if (windowRef === null || windowRef.closed) {
      /* if the pointer to the window object in memory does not exist
      or if such pointer exists but the window was closed */
      setWindowRef(window.open(url, name, strWindowFeatures));
    } else if (previousUrl !== url) {
      /* if the resource to load is different,
      then we load it in the already opened secondary window and then
      we bring such window back on top/in front of its parent window. */
      setWindowRef(window.open(url, name, strWindowFeatures));
      windowRef.focus();
    } else {
      /* else the window reference must exist and the window
      is not closed; therefore, we can bring it back on top of any other
      window with the focus() method. There would be no need to re-create
      the window or to reload the referenced resource. */
      windowRef.focus();
    }

    // add the listener for receiving a message from the popup
    window.addEventListener('message', event => receiveMessage(event), false);
    // assign the previous URL
    setPreviousUrl(url);
  };

  const handleOffice365Click = () => {
    (window as any).analytics.track('Clicked Connect Calendar Button in Onboarding', { button: 'office365' });
    trackClickedConnectApp(DataSourceSlugs.office365Calendar, true);

    if (openWindow)
      openSignInWindow(
        office365CalendarSignupUrl +
          '&redirect_url=/oauth2/window_redirect&failure_redirect_url=/oauth2/window_redirect',
        'Connect your Calendar'
      );
  };

  const handleGoogleClick = () => {
    (window as any).analytics.track('Clicked Connect Calendar Button in Onboarding', { button: 'google' });
    trackClickedConnectApp(DataSourceSlugs.googleCalendarWorkspaceSync, true);

    if (openWindow)
      openSignInWindow(
        googleCalendarSignupProfileScopeUrl +
          '&redirect_url=/oauth2/window_redirect&failure_redirect_url=/oauth2/window_redirect',
        'Connect your Calendar'
      );
  };

  const showSignInButton = errorMessage && errorMessage.includes('Please sign in.');

  if (showSignInButton) {
    return (
      <div className="mb-3 mt-2">
        <WpAlert type="danger">
          <div className="ml-2 mt-2">{errorMessage.replace('Please sign in.', '')}</div>
          <div className="ml-2 mt-2 mb-2">
            <WpButton onClick={() => (window.location.href = '/')}>Sign in</WpButton>
          </div>
        </WpAlert>
      </div>
    );
  }

  return (
    <div>
      {errorMessage && (
        <div className="mb-3">
          <WpAlert type="danger">{errorMessage}</WpAlert>
        </div>
      )}
      <WpButtonGroup stacked>
        {!cannotSignIntoGoogle && (
          <GoogleButton
            googleSignUpUrl={openWindow ? '#' : googleCalendarSignupProfileScopeUrl}
            handleClick={handleGoogleClick}
          >
            Connect Google Calendar
          </GoogleButton>
        )}
        <Office365Button
          office365SignUpUrl={openWindow ? '#' : office365CalendarSignupUrl}
          handleClick={handleOffice365Click}
        >
          Connect Office365 Calendar
        </Office365Button>
      </WpButtonGroup>
    </div>
  );
}

export default ConnectCalendarButtons;
